import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ActionButton, Buttons, CancelButton, Drawer, Table } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm, useIsMounted } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import OptionSelect from './OptionSelect';

export default function InvoiceItemMappings({ onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();
  const [{ isSubmitting, saved }, form] = useForm();
  const isMounted = useIsMounted();
  const [dirty, setDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => dirty);
  const { sageConnectionId } = useParams();

  const [{ invoiceItems, items, orderEntryItems, departments, isOrderEntry, isReady }, setState] = useState({
    invoiceItems: null,
    isReady: false,
  });

  useEffect(() => {
    (async () => {
      const sage = api.www.workspaces(workspace.id).sage(sageConnectionId);
      const { data: invoiceItems } = await sage.getInvoiceItemMappings();
      const { data: items } = await sage.getItems();
      const { data: departments } = await sage.getDepartments();
      const { data: configuration } = await sage.getConfiguration();
      const { data: orderEntryItems } = await sage.getOrderEntryItems();
      if (!isMounted.current) return;
      setState({
        invoiceItems,
        items,
        orderEntryItems,
        departments,
        isOrderEntry: configuration?.syncMethod === 'documents',
        isReady: true,
      });
    })();
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const updateMapping = ({ invoiceItemId, ...patch }) => {
    setDirty(true);
    setState((state) => ({
      ...state,
      invoiceItems: state.invoiceItems.map((i) => (i.id === invoiceItemId ? { ...i, ...patch } : i)),
    }));
  };

  return (
    <Drawer
      isOpen
      title="Invoice Item Mappings"
      width="70rem"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        const handleSave = async () => {
          form.submit();
          try {
            const mappings = invoiceItems.map((i) => ({
              invoiceItemId: i.id,
              sageIncomeAccountId: i.sageIncomeAccountId,
              sageDepartmentId: i.sageDepartmentId,
              sageItemKey: i.sageItemKey,
            }));
            await api.www.workspaces(workspace.id).sage(sageConnectionId).updateInvoiceItemMappings(mappings);
            form.save();
            closeDrawer();
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        };

        if (!isReady) return <PageLoader />;

        return (
          <>
            <Table style={{ marginBottom: '2rem' }}>
              <Table.BoxHeader>
                <Table.Column width="40%">Invoice Item</Table.Column>
                <Table.Column>{isOrderEntry ? 'Sage Item' : 'Sage Income Account'}</Table.Column>
                <Table.Column>Sage Department</Table.Column>
              </Table.BoxHeader>
              <Table.Body>
                {invoiceItems.map((invoiceItem) => (
                  <Table.BoxRow key={invoiceItem.id}>
                    <Table.Cell>{invoiceItem.name}</Table.Cell>
                    <Table.Cell>
                      {isOrderEntry ? (
                        <OptionSelect
                          defaultOptions={orderEntryItems}
                          typeLabel="Sage Item"
                          value={invoiceItem.sageItemKey}
                          onChange={(event) =>
                            updateMapping({
                              invoiceItemId: invoiceItem.id,
                              sageItemKey: event.target.value || null,
                              ...(event.target.value ? {} : { sageDepartmentId: null }),
                            })
                          }
                        />
                      ) : (
                        <OptionSelect
                          defaultOptions={items}
                          typeLabel="Sage Income Account"
                          value={invoiceItem.sageIncomeAccountId}
                          onChange={(event) =>
                            updateMapping({
                              invoiceItemId: invoiceItem.id,
                              sageIncomeAccountId: event.target.value || null,
                              ...(event.target.value ? {} : { sageDepartmentId: null }),
                            })
                          }
                        />
                      )}
                    </Table.Cell>
                    <Table.Cell>
                      <OptionSelect
                        defaultOptions={departments}
                        typeLabel="Sage Department"
                        disabled={isOrderEntry ? !invoiceItem.sageItemKey : !invoiceItem.sageIncomeAccountId}
                        value={invoiceItem.sageDepartmentId}
                        onChange={(event) =>
                          updateMapping({
                            invoiceItemId: invoiceItem.id,
                            sageDepartmentId: event.target.value || null,
                          })
                        }
                      />
                    </Table.Cell>
                  </Table.BoxRow>
                ))}
              </Table.Body>
            </Table>
            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                <ActionButton isLoading={isSubmitting} ok={saved} onClick={handleSave}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}
